import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  OrganizationTagRow,
  organizationTagsService,
} from "@/services/organizationTags.service/organizationTags.api";
import { Check, Loader2, Pencil, Search, Tag as TagIcon, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

export default function TagsSettings() {
  const [tags, setTags] = useState<OrganizationTagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [result] = await organizationTagsService.listManage();
    if (result) setTags(result);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredTags = tags.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()));

  const startEditing = (tag: OrganizationTagRow) => {
    setEditingId(tag._id);
    setEditingValue(tag.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleRename = async (id: string) => {
    if (!editingValue.trim()) return;
    setActingId(id);
    const [, err] = await organizationTagsService.rename(id, editingValue.trim());
    setActingId(null);
    if (err) {
      toast.error((err as { msg?: string })?.msg || "Erro ao renomear tag");
      return;
    }
    toast.success("Tag renomeada — atualizado em todo lugar que já usava ela.");
    cancelEditing();
    void load();
  };

  const handleDelete = async (id: string) => {
    setActingId(id);
    const [, err] = await organizationTagsService.remove(id);
    setActingId(null);
    if (err) {
      toast.error((err as { msg?: string })?.msg || "Erro ao apagar tag");
      return;
    }
    toast.success("Tag apagada — removida de todo lugar que já usava ela.");
    void load();
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar tag..."
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tag</TableHead>
              <TableHead className="w-32 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={2} className="text-center py-8 text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            ) : filteredTags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <TagIcon className="h-8 w-8 text-muted-foreground" />
                    <p className="text-muted-foreground">Nenhuma tag encontrada</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredTags.map((tag) => {
                const isEditing = editingId === tag._id;
                const isActing = actingId === tag._id;
                return (
                  <TableRow key={tag._id}>
                    <TableCell className="font-medium">
                      {isEditing ? (
                        <Input
                          autoFocus
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void handleRename(tag._id);
                            if (e.key === "Escape") cancelEditing();
                          }}
                          className="h-8 max-w-xs"
                        />
                      ) : (
                        tag.name
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {isEditing ? (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              disabled={isActing}
                              onClick={() => void handleRename(tag._id)}
                              aria-label="Salvar"
                            >
                              {isActing ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Check className="h-3.5 w-3.5" />
                              )}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              disabled={isActing}
                              onClick={cancelEditing}
                              aria-label="Cancelar"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              disabled={isActing}
                              onClick={() => startEditing(tag)}
                              aria-label="Renomear"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  disabled={isActing}
                                  aria-label="Apagar"
                                >
                                  {isActing ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Apagar tag "{tag.name}"?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Remove essa tag de todos os contatos, tickets e empresas que já a
                                    usam, além de tirar do pool de sugestões. Essa ação não pode ser
                                    desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => void handleDelete(tag._id)}>
                                    Apagar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
